/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
// Store API utilities for customer authentication and cart management

// ✅ TenantDelivery API
const normalizeDeliveryEntry = (item = {}) => ({
  deliveryId: item.DeliveryId ?? item.deliveryId,
  tenantId: item.TenantId ?? item.tenantId,
  deliveryCompanyName: (item.DeliveryCompanyName ?? item.deliveryCompanyName ?? "").replace(/^"|"$/g, "").trim(),
  deliveryCompanyFees: Number(item.DeliveryCompanyFees ?? item.deliveryCompanyFees ?? 0),
  cityId: Number(item.CityId ?? item.cityId ?? 0),
  cityName: (item.CityName ?? item.cityName ?? "").replace(/^"|"$/g, "").trim(),
  isActive: item.IsActive ?? item.isActive ?? false
});

export const isDeliveryCompanyActive = (item) =>
  item?.isActive === true ||
  item?.isActive === 1 ||
  String(item?.isActive).toLowerCase() === "true";

const buildTenantDeliveryPayload = (tenantId, payload = {}) => ({
  tenantId: Number(tenantId),
  deliveryCompanyName: String(payload.deliveryCompanyName || "").trim(),
  isActive: !!payload.isActive,
  cities: Array.isArray(payload.cities)
    ? payload.cities.map((city) => ({
        cityId: Number(city.cityId ?? 0),
        cityName: String(city.cityName || "").trim(),
        fees: Number(city.fees ?? city.deliveryCompanyFees ?? 0)
      }))
    : []
});

export const groupDeliveryCompanies = (items = []) => {
  const groups = new Map();

  items
    .map(normalizeDeliveryEntry)
    .filter((item) => item.deliveryCompanyName)
    .forEach((item) => {
      const companyKey = item.deliveryCompanyName.toLowerCase();
      const existing = groups.get(companyKey);

      if (!existing) {
        groups.set(companyKey, {
          companyKey,
          deliveryCompanyName: item.deliveryCompanyName,
          tenantId: item.tenantId,
          isActive: isDeliveryCompanyActive(item),
          deliveryIds: item.deliveryId ? [item.deliveryId] : [],
          cities: [
            {
              cityId: item.cityId,
              cityName: item.cityName,
              fees: item.deliveryCompanyFees,
              deliveryId: item.deliveryId
            }
          ]
        });
        return;
      }

      existing.isActive = existing.isActive || isDeliveryCompanyActive(item);
      if (item.deliveryId) {
        existing.deliveryIds.push(item.deliveryId);
      }
      existing.cities.push({
        cityId: item.cityId,
        cityName: item.cityName,
        fees: item.deliveryCompanyFees,
        deliveryId: item.deliveryId
      });
    });

  return Array.from(groups.values())
    .map((company) => {
      const uniqueCities = new Map();

      company.cities.forEach((city) => {
        if (!city.cityId) return;
        uniqueCities.set(city.cityId, city);
      });

      return {
        ...company,
        deliveryIds: [...new Set(company.deliveryIds)],
        cities: Array.from(uniqueCities.values()).sort((a, b) => a.cityId - b.cityId)
      };
    })
    .sort((a, b) => {
      if (a.isActive !== b.isActive) {
        return a.isActive ? -1 : 1;
      }
      return a.deliveryCompanyName.localeCompare(b.deliveryCompanyName, "ar");
    });
};

export const pickActiveDeliveryCompany = (data) => {
  const entries = (Array.isArray(data) ? data : [data])
    .filter(Boolean)
    .map(normalizeDeliveryEntry);

  const activeEntry = entries.find(isDeliveryCompanyActive);
  return activeEntry || null;
};

export const getDeliveryCompanies = async (tenantId) => {
  try {
    const res = await fetch(`${API_BASE_URL}/TenantDelivery/GetAll?tenantId=${tenantId}`);
    const data = await safeJson(res);
    return Array.isArray(data) ? groupDeliveryCompanies(data) : [];
  } catch { return []; }
};

export const addDeliveryCompany = async (tenantId, payload) => {
  try {
    const res = await fetch(`${API_BASE_URL}/TenantDelivery/Add`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(buildTenantDeliveryPayload(tenantId, payload))
    });
    return res.ok;
  } catch { return false; }
};

export const updateDeliveryCompany = async (tenantId, payload) => {
  try {
    const res = await fetch(`${API_BASE_URL}/TenantDelivery/Update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(buildTenantDeliveryPayload(tenantId, payload))
    });
    return res.ok;
  } catch { return false; }
};

export const deleteDeliveryCompany = async (tenantId, deliveryIds) => {
  try {
    const ids = Array.isArray(deliveryIds) ? deliveryIds : [deliveryIds];
    const validIds = ids.filter(Boolean);
    if (validIds.length === 0) return false;

    const results = await Promise.all(
      validIds.map((deliveryId) =>
        fetch(`${API_BASE_URL}/TenantDelivery/Delete?tenantId=${tenantId}&deliveryId=${deliveryId}`, {
          method: "DELETE"
        })
      )
    );

    return results.every((res) => res.ok);
  } catch { return false; }
};

export const getDeliveryByCity = async (tenantId, cityId) => {
  try {
    const res = await fetch(`${API_BASE_URL}/TenantDelivery/GetByCity?tenantId=${tenantId}&cityId=${cityId}`);
    const data = await safeJson(res);
    return pickActiveDeliveryCompany(data);
  } catch { return null; }
};

const API_BASE_URL =
  (process.env.REACT_APP_API_BASE_URL ||
    "https://madar-api.madar.iq/api").replace(/\/+$/, "");

// ✅ Helper: safe JSON
// ✅ Safe JSON that handles empty responses
const safeJson = async (res) => {
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const text = await res.text();
  if (!text) return {};          // السيرفر رجّع 200 بدون محتوى

  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};



const API_HOST = API_BASE_URL.replace("/api", "");

const buildImageUrl = (url) => {
  if (!url) return "https://placehold.co/400x400.png?text=No+Image";
  if (url.startsWith("http")) return url;
  return `${API_HOST}${url}`;
};

// ================= STORE DATA =================
export const getTags = async (tenantId) => {
  try {
    const res = await fetch(`${API_BASE_URL}/Tags/get?tenantId=${tenantId}`);
    const data = await safeJson(res);
    return { success: true, data: Array.isArray(data) ? data : [] };
  } catch {
    return { success: false, data: [] };
  }
};
export const getCurrentOrder = () => {
  try {
    const raw = localStorage.getItem("storeCurrentOrder");
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return parsed?.orderId ? parsed : null;

  } catch {
    return null;
  }
};


// ================= STORE REVIEWS =================

export const getStoreReviews = async (tenantId) => {
  try {
    const res = await fetch(
      `${API_BASE_URL}/Storereview/GetStoreReviews?tenantId=${tenantId}`
    );

    const data = await safeJson(res);

    const mapped = Array.isArray(data)
      ? data
        .filter(r => (r.isActive ?? r.IsActive) === 1 || (r.isActive ?? r.IsActive) === true)
        .map(r => ({
          id: r.Id ?? r.id,
          tenantId: r.TenantId ?? r.tenantId,
          customerName: r.customerName ?? r.CustomerName,
          hasImage: r.HasImage ?? r.hasImage,
          imageUrl: r.ImageUrl ?? r.imageUrl,
          rating: r.Rating ?? r.rating,
          createDate: r.CreateDate ?? r.createDate,
          isActive: r.IsActive ?? r.isActive,
          description: r.Description ?? r.description
        }))
      : [];

    return { success: true, data: mapped };
  } catch {
    return { success: false, data: [] };
  }
};
export const addStoreReview = async (review) => {
  try {
    const hasImage = !!review.imageUrl;

    const payload = {
      id: 0,
      tenantId: Number(review.tenantId),
      hasImage,
      imageUrl: hasImage ? review.imageUrl : "",
      rating: Number(review.rating),
      customerName: String(review.customerName || "").trim(),
      description: String(review.description || "").trim(),
      createDate: new Date().toISOString(),
      isActive: 1
    };

    const res = await fetch(
      `${API_BASE_URL}/Storereview/AddStoreReviews`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      }
    );

    const data = await safeJson(res);
    return { success: true, data };
  } catch {
    return { success: false, error: "فشل إضافة التقييم" };
  }
};

const asText = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  return String(value).trim();
};

const asNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

// أضفنا tenantId كمعامل أول للدالة
export const buildUpdateOrderPayload = (tenantId, order, customer, city, coupon) => {
  if (!tenantId || Number(tenantId) <= 0) throw new Error("TenantID is required");

  const sourceItems = Array.isArray(order.items) ? order.items : Array.isArray(order.details) ? order.details : [];
  const normalizedItems = sourceItems.map((i) => ({
    id: Number(i.id || 0),
    invoiceNo: String(i.invoiceNo || ""),
    itemId: Number(i.itemId || 0),
    variantId: Number(i.variantId || 0),
    itemName: String(i.itemName || ""),
    variantName: String(i.variantName || ""),
    colorName: String(i.color || i.colorHex || i.colorName || ""),
    size: String(i.size || ""),
    quantity: Number(i.quantity || 0),
    price: Number(i.price || 0),
    imageUrl: String(i.imageUrl || ""),
    stockQty: Number(i.stockQty || 0),
    discount: Number(i.discount || 0),
    lineTotal: Number(i.lineTotal || (Number(i.quantity) * Number(i.price))),
     costPrice: Number(i.costPrice ?? i.originalVariant?.costPrice ?? 0)
  }));

  return {
    OrderId: String(order.orderId || order.OrderId || ""),
    orderId: String(order.orderId || order.OrderId || ""),
    orderNo: Number(order.orderNo || 0),
    // الإصلاح هنا: نستخدم tenantId المرر للدالة مباشرة
    tenantID: Number(tenantId),
    tenantId: Number(tenantId),
    statusId: Number(order.statusId || 1),
    status: String(order.status || "new"),
    totalAmount: Number(order.totalAmount || 0),
    discountAmount: Number(order.discountAmount || 0),
    netAmount: Number(order.netAmount || 0),
    deliveryId: Number(order.deliveryId || 0),
    customerId: Number(customer.customerId || 0),
    customerName: String(order.customerName || customer?.name || ""),
    cityId: Number(city?.cityId || customer?.cityId || 0),
    city: String(city?.cityName || order.city || ""),
    address: String(order.address || customer?.address || ""),
    customerPhone1: String(order.customerPhone1 || customer?.phone || ""),
    paymentMethodName: String(order.paymentMethodName || "Cash"),
    orderDate: order.orderDate || new Date().toISOString(),
    transactionId: String(order.transactionId || ""),
    coupon: String(coupon?.code || order.coupon || ""),
    paymentStatus: String(order.paymentStatus || "new"),
    isPaid: Number(order.isPaid || 0),
    isManual: 0,
    details: normalizedItems
  };
};





export const getTenantIdentityBySlug = async (storeSlug) => {
  try {
    const res = await fetch(
      `${API_BASE_URL}/TenantIdentity/GetBySlug?storeslug=${encodeURIComponent(storeSlug)}`
    );

    const data = await safeJson(res);

    if (!Array.isArray(data) || data.length === 0) {
      return { success: false, error: "Tenant not found" };
    }

    const t = data[0];

    return {
      success: true,
      data: {
        tenantId: Number(t.tenantId),
        storeName: t.storeName || "",
        storeSlogan: t.storeSlogan || "",
        logo: t.logoUrl ? `https://madar-api.madar.iq${t.logoUrl}` : "",
        themeId: Number(t.themeId || 1),
        colorName: t.colorName || t.ColorName || ""   // ← جديد

      }
    };

  } catch (e) {
    console.error("getTenantIdentityBySlug error:", e);
    return { success: false, error: "TenantIdentity API failed" };
  }
};


export async function getItems(tenantId) {
  try {
    const res = await fetch(`${API_BASE_URL}/GetItems?tenantId=${tenantId}&itemId=-1`);
    const data = await safeJson(res);

    if (!Array.isArray(data)) return { success: true, data: [] };

    const normalized = data.map(item => {
      // 1. استخراج قيم الخصم الأساسية للمنتج (دعم كل الحالات الممكنة للمسميات من السيرفر)
      const dValue = Number(item.discountValue || item.discount || item.DiscountValue || item.discount_value || 0);
      const dType = Number(item.discountType || item.DiscountType || item.discount_type || 0);
      const itemCode = item.itemCode || item.ItemCode || item.item_code || item.sku || "";

      const variants = (item.variants || []).map(v => {
        const basePrice = Number(v.price || 0);
        let calculatedFinalPrice = basePrice;
        let discountAmount = 0;

        // 2. حساب السعر الصافي بناءً على النوع
        if (dType === 1) { // 1 = خصم نسبة مئوية
          discountAmount = (basePrice * dValue) / 100;
          calculatedFinalPrice = basePrice - discountAmount;
        } else { // 0 = خصم مبلغ ثابت
          discountAmount = dValue;
          calculatedFinalPrice = basePrice - dValue;
        }

        return {
          variantId: Number(v.variantId),
          variantName: v.variantName,
          color: (() => {
            const rawHex = String(v.color?.hexCode || v.color?.hex || "").trim();
            const colorName = String(v.color?.name || "").trim();
            const colorId = Number(v.color?.id || 0);
            const hasColor = Boolean(rawHex || colorName || colorId > 0);
            if (!hasColor) return null;
            return {
              id: colorId,
              name: colorName,
              hex: rawHex,
              hexCode: rawHex,
            };
          })(),
          price: basePrice, // السعر الأصلي
          costPrice: Number(v.costPrice || 0), // ✅ سعر التكلفة من الـ API
          discount: discountAmount, // قيمة الخصم المحسوبة
          finalPrice: calculatedFinalPrice, // السعر الصافي
          options: (v.sizes || []).map(s => ({
            label: s.size,
            stock: Number(s.stockQty || 0)
          })),
          stockQty: Number(v.stockQty || (v.sizes || []).reduce((acc, s) => acc + Number(s.stockQty || 0), 0)),
          imageUrl: v.imageUrl
        };
      });

      return {
        id: Number(item.itemId),
        itemCode: itemCode, // كود المنتج المستخرج
        name: item.itemName,
        description: item.description,
        mainImage: item.mainImageUrl,
        variants: variants,
        discountValue: dValue,
        discountType: dType,
        tenantId: item.tenantId
      };
    });

    return { success: true, data: normalized };
  } catch (error) {
    console.error("Fetch Items Error:", error);
    return { success: false, data: [] };
  }
}







export const getCategories = async (tenantId) => {
  try {
    const res = await fetch(
      `${API_BASE_URL}/TenantCategory/GetCategories?TenantId=${tenantId}`
    );

    const data = await safeJson(res);

    const normalized = Array.isArray(data)
      ? data.map(cat => ({

        categoryId: cat.categoryId,
        categoryName: cat.categoryName,
        description: cat.description,
        createdAt: cat.createdAt,
        isActive: cat.isActive,

        imageUrl: cat.imageUrl
          ? `${API_BASE_URL.replace('/api', '')}/uploads/${tenantId}/categories/${cat.imageUrl}`
          : "https://placehold.co/400x400.png?text=No+Image",

        // ⭐ أضف هذا
        items: Array.isArray(cat.items) ? cat.items : [],

        subCategories: (cat.subCategories || []).map(sub => ({
          subCategoryId: sub.subCategoryId,
          subCategoryName: sub.subCategoryName,
          description: sub.description,
          productsCount: sub.productsCount || 0,
          items: Array.isArray(sub.items) ? sub.items : []
        }))
      }))
      : [];

    return { success: true, data: normalized };

  } catch {
    return { success: false, error: "فشل جلب الفئات", data: [] };
  }
};

export const getBanners = async (tenantId) => {
  try {
    const res = await fetch(`${API_BASE_URL}/Banners/GetBanners?tenantId=${tenantId}`);
    const data = await safeJson(res);
    return { success: true, data: Array.isArray(data) ? data : [] };
  } catch {
    return { success: false, error: "فشل جلب البانرات", data: [] };
  }
};

export const getCoupons = async (tenantId) => {
  try {
    const res = await fetch(`${API_BASE_URL}/Coupons/GetCoupons?tenantId=${tenantId}`);
    const data = await safeJson(res);
    return { success: true, data: Array.isArray(data) ? data : [] };
  } catch {
    return { success: false, error: "فشل جلب الكوبونات", data: [] };
  }
};

// ================= CUSTOMER =================

export const registerCustomer = async (customerData) => {
  try {
    const res = await fetch(`${API_BASE_URL}/Customer/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({
        tenantId: Number(customerData.tenantId),
        name: customerData.name,
        email: customerData.email,
        password: customerData.password,
        phone: customerData.phone,
        address: customerData.address,
        cityId: Number(customerData.cityId),
        city: customerData.cityName
      }),
    });

    // إذا كانت الاستجابة ناجحة (200)
    if (res.ok) {
      const data = await res.json();
      return {
        success: true,
        customerId: data.customerId,
        token: data.token,
        cityId: customerData.cityId,
        city: customerData.cityName,
        message: data.message
      };
    }

    // إذا كان هناك خطأ (مثل 400)
    // نقرأ الاستجابة كنص لأن السيرفر يرسل "Email already registered."
    const errorText = await res.text();
    return {
      success: false,
      error: errorText || "فشل التسجيل"
    };

  } catch (error) {
    console.error("Register Error:", error);
    return { success: false, error: "حدث خطأ في الشبكة" };
  }
};

// ✅ LOGIN مصحح 100%
export const loginCustomer = async (loginData) => {
  try {
    const res = await fetch(`${API_BASE_URL}/Customer/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(loginData),
    });

    const data = await res.json();

    if (!res.ok) {
      return { success: false, error: data?.message || "فشل تسجيل الدخول" };
    }

    return {
      success: true,
      customerId: data.customerId,
      token: data.token,
      name: data.name,
      email: data.email,
      phone: data.phone,
      address: data.address,
      city: data.city || "",
      cityId: data.cityId || null,
      tenantId: data.tenantId,
      isActive: data.isActive
    };



  } catch (e) {
    return { success: false, error: e.message };
  }
};

// ✅ LOGOUT الآمن
export const logoutCustomer = () => {
  localStorage.removeItem("storeCustomer");
  localStorage.removeItem("storeCustomerId");
};

export const getCurrentCustomer = () => {
  try {
    const c = JSON.parse(localStorage.getItem("storeCustomer"));
    if (!c || !c.customerId) return null;

    return {
      ...c,
      cityId: Number(c.cityId) || null,
      cityName: c.cityName || c.city || ""
    };
  } catch {
    return null;
  }
};


export const getCurrentCustomerId = () => {
  return Number(localStorage.getItem("storeCustomerId")) || null;
};

export const isCustomerLoggedIn = () => {
  return !!getCurrentCustomerId();
};

// ================= CART =================
// storeApi.js
// داخل ملف storeApi.js
// داخل storeApi.js
// داخل storeApi.js
// داخل storeApi.js
export const addToCart = async (variant, qty, tenantId, customerId) => {
  try {
    const payload = {
      tenantId: Number(tenantId),
      customerId: Number(customerId),
      variantId: Number(variant.variantId),
      quantity: Number(qty),
      // السعر الذي نستقبله هنا هو السعر الصافي الجاهز
      price: Number(variant.price),
      discount: Number(variant.discount || 0),
      color: String(variant.color || variant.colorHex || variant.hex || variant.colorName || ""),
      size: String(variant.size || ""),
      memory: ""
    };

    const res = await fetch(`${API_BASE_URL}/Cart/AddToCart`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    return await safeJson(res);
  } catch (error) {
    return { success: false };
  }
};



export const getCart = async (tenantId, customerId) => {
  try {
    const res = await fetch(
      `${API_BASE_URL}/Cart/GetCart?tenantId=${tenantId}&customerId=${customerId}`
    );
    const data = await safeJson(res);

    return data; // نعيد الاستجابة كاملة كما هي
  } catch {
    return { success: false, cartData: { cart: {}, items: [] } };
  }
};


export const removeFromCart = async (tenantId, orderId, variantId, customerId) => {
  const payload = { tenantId, orderId, variantId, customerId };

  const res = await fetch(`${API_BASE_URL}/Cart/RemoveFromCart`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });

  const data = await safeJson(res);
  if (!data.success) throw data;
  return data;
};

export const updateTenantOrder = async (tenantId, order, city) => {
  // التحقق من الحالة قبل الإرسال (اختياري حسب منطق عملك)
  if (order.statusId && Number(order.statusId) !== 1) {
    return { success: false, error: "لا يمكن تعديل الطلب بعد بدء التجهيز" };
  }

  const customer = getCurrentCustomer();
  if (!customer?.customerId) {
    return { success: false, error: "يرجى تسجيل الدخول أولاً" };
  }

  try {
    // جلب الكوبون من الـ LocalStorage إذا لم يكن مرراً
    const coupon = JSON.parse(localStorage.getItem("storeCoupon") || "null");

    // بناء الـ Payload
    const payload = buildUpdateOrderPayload(tenantId, order, customer, city, coupon);

    const res = await fetch(
      `${API_BASE_URL}/UpdateTenantOrder?tenantid=${tenantId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(payload)
      }
    );

    // معالجة الاستجابة
    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(`Error ${res.status}: ${errorText}`);
    }

    const data = await safeJson(res);

    // مسح الكوبون بعد نجاح الطلب
    localStorage.removeItem("storeCoupon");

    return { success: true, data };

  } catch (e) {
    console.error("updateTenantOrder error:", e);
    return {
      success: false,
      error: e.message || "فشل الاتصال بالسيرفر، يرجى المحاولة لاحقاً"
    };
  }
};









export const getTenantDetails = async (tenantId) => {
  try {
    const res = await fetch(
      `${API_BASE_URL}/GetAllTenantFullDetails?tenantId=${tenantId}`
    );

    const data = await safeJson(res);

    if (!Array.isArray(data)) {
      return { success: false, error: "Invalid response format" };
    }

    const tenant = data.find(t => Number(t.TenantId) === Number(tenantId));

    if (!tenant) {
      return { success: false, error: "Tenant not found" };
    }

    return { success: true, data: tenant };

  } catch (err) {
    console.error("getTenantDetails error:", err);
    return { success: false, error: "فشل جلب تفاصيل المتجر" };
  }
};
export const normalizeTenantDetails = (t = {}) => {
  const rawLogo = t.Logo || t.logo || "";
  const tenantId = Number(t.TenantId || t.tenantId || 0);
  const description =
    t.BusinessName ||
    t.businessName ||
    t.Description ||
    t.description ||
    t.Descripsion ||
    t.EngDescripsion ||
    "";

  let logo = "";
  if (rawLogo) {
    if (rawLogo.startsWith("http")) {
      logo = rawLogo;
    } else {
      // rawLogo = "/TenantDtl/2/fashion.png"
      const parts = rawLogo.replace(/^\/+/, "").split("/");
      // ["TenantDtl","2","fashion.png"]

      if (parts.length >= 3) {
        const folder = parts[0];       // TenantDtl
        const fileId = parts[1];       // 41
        const fileName = parts.slice(2).join("/");

        logo = `https://madar-api.madar.iq/uploads/${fileId}/${folder}/${fileName}`;
      }
    }
  }

  return {
    tenantId,

    storeName:
      t.ClientName ||
      t.clientName ||
      "",

    storeSlug: t.storeSlug || t.SubDomain || "",
    sectorType: t.SectorType || "",
    description,
    themeId: Number(t.ThemeId || 1),

    logo,

    phone: t.Phone || "",
    email: t.Email || "",
    colorName: t.ColorName || "",

    social: {
      facebook: t.facebook || "",
      instagram: t.instagram || "",
      snapchat: t.snapchat || "",
      tiktok: t.tiktok || "",
      whatsapp: t.whatsapp || ""
    },

    policies: {
      privacy: t.privacyPolicy || "",
      delivery: t.deliveryPolicy || "",
      refund: t.refundPolicy || ""
    }
  };
};





export const getCities = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/City/GetCities`);
    const data = await safeJson(res);
    return { success: true, data: Array.isArray(data) ? data : [] };
  } catch {
    return { success: false, data: [] };
  }
};
export const logStoreVisit = async (tenantId) => {
  try {
    await fetch(`${API_BASE_URL}/VisitsCount`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId })
    });
  } catch { }
};

export const getStoreVisits = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/VisitsCount`);
    return await safeJson(res);
  } catch {
    return null;
  }
};
export const getPaymentMethods = async (tenantId) => {
  try {
    const res = await fetch(
      `${API_BASE_URL}/PaymentMethods/GetPaymentMethods?tenantId=${tenantId}`
    );
    const data = await safeJson(res);
    return { success: true, data };
  } catch {
    return { success: false, data: [] };
  }
};
export const getCustomerOrders = async (tenantId, customerId) => {
  try {
    const res = await fetch(
      `${API_BASE_URL}/GetTenantOrders?tenantId=${tenantId}&statusId=-1&OrderNo=-1&CustomerId=${customerId}`
    );

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();

    const uniqueOrders = [];
    const seenIds = new Set();
    if (Array.isArray(data)) {
      data.forEach(o => {
        if (o.orderId && !seenIds.has(o.orderId)) {
          seenIds.add(o.orderId);
          uniqueOrders.push(o);
        }
      });
    }

    const mapped = uniqueOrders
      .filter(o => Number(o.customerId) === Number(customerId))
      .map(o => ({
        orderId: o.orderId,
        orderNo: o.orderNo,
        tenantId: o.tenantID,

        statusId: Number(o.statusId),
        status: o.status,

        orderDate: o.orderDate,

        cityId: Number(o.cityId),        // ⭐
        city: o.city,
        address: o.address,

        customerId: Number(o.customerId), // ⭐
        customerName: o.customerName,     // ⭐
        customerPhone1: o.customerPhone1,
        paymentMethodName: o.paymentMethodName || "Cash", // ⭐

        netAmount: Number(o.netAmount || 0),
        totalAmount: Number(o.totalAmount || 0),
        discountAmount: Number(o.discountAmount || 0),

        paymentStatus: o.paymentStatus,
        isPaid: Number(o.isPaid || 0),
        coupon: o.coupon || "",

        details: Array.isArray(o.details) ? o.details : []
      }))


    return { success: true, data: mapped };

  } catch (e) {
    console.error("getCustomerOrders error:", e);
    return { success: false, data: [] };
  }
};






export const createTenantOrder = async (tenantId, city, orderData = null, couponData = null) => {
  const order = orderData || getCurrentOrder();
  const customer = getCurrentCustomer();

  try {
    // مررنا tenantId هنا كأول parameter
    const payload = buildUpdateOrderPayload(tenantId, order, customer, city, couponData);

    const res = await fetch(
      `${API_BASE_URL}/UpdateTenantOrder?tenantid=${tenantId}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      }
    );

    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.message || `HTTP ${res.status}`);
    }

    const data = await safeJson(res);
    localStorage.removeItem("storeCoupon");
    return { success: true, data };

  } catch (e) {
    console.error("createTenantOrder error:", e);
    return { success: false, error: e.message };
  }
};

const isRedirectablePaymentUrl = (value) => {
  if (typeof value !== "string") return false;
  const t = value.trim();
  if (!t) return false;
  if (/^https?:\/\//i.test(t)) return true;
  if (t.startsWith("//") && t.length > 2) return true;
  if (t.startsWith("/") && t.length > 1) return true;
  return false;
};

const extractPaymentUrl = (data) => {
  if (!data) return "";
  if (typeof data === "string" && isRedirectablePaymentUrl(data)) return data.trim();

  const directCandidates = [
    data.url,
    data.link,
    data.paymentUrl,
    data.paymentLink,
    data.redirectUrl,
    data.checkoutUrl
  ];

  const nestedCandidates = [
    data.data?.url,
    data.data?.link,
    data.data?.paymentUrl,
    data.data?.paymentLink,
    data.data?.redirectUrl,
    data.data?.checkoutUrl,
    data.result?.url,
    data.result?.paymentUrl,
    data.result?.redirectUrl
  ];

  const allCandidates = [...directCandidates, ...nestedCandidates];
  const found = allCandidates.find((value) => isRedirectablePaymentUrl(value));
  return found ? String(found).trim() : "";
};

export const initiatePayment = async ({
  tenantId,
  orderId,
  email,
  phone,
  firstName,
  lastName
}) => {
  try {
    const payload = {
      tenantId: Number(tenantId) || 0,
      orderId: String(orderId || ""),
      email: String(email || ""),
      phone: String(phone || ""),
      firstName: String(firstName || ""),
      lastName: String(lastName || "")
    };

    const res = await fetch(`${API_BASE_URL}/Payment/initiate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*"
      },
      body: JSON.stringify(payload)
    });

    const data = await safeJson(res);
    const paymentUrl = extractPaymentUrl(data);

    if (!paymentUrl) {
      return { success: false, error: "لم يتم إرجاع رابط الدفع من مزود الدفع", data };
    }

    return { success: true, paymentUrl, data };
  } catch (e) {
    console.error("initiatePayment error:", e);
    return { success: false, error: e.message || "فشل بدء عملية الدفع" };
  }
};

export const getSubscriptionPaymentContact = () => {
  const tenant = JSON.parse(localStorage.getItem("tenant") || "{}");
  const tenantDetails = JSON.parse(localStorage.getItem("tenantDetails") || "{}");
  const reg = JSON.parse(localStorage.getItem("fullRegisterResponse") || "{}");

  const fullName =
    tenant.tenantName ||
    tenant.engTenantName ||
    tenantDetails.companyName ||
    reg?.tenant?.tenantName ||
    localStorage.getItem("companyStoreName") ||
    "";

  const trimmed = String(fullName).trim();
  const [firstName = "", ...lastNameParts] = trimmed.split(/\s+/);

  return {
    email:
      localStorage.getItem("tenantEmail") ||
      tenant.email ||
      tenantDetails.email ||
      reg?.tenant?.email ||
      "",
    phone:
      localStorage.getItem("tenantPhone") ||
      tenant.phone ||
      tenantDetails.phone ||
      reg?.tenant?.phone ||
      "",
    firstName,
    lastName: lastNameParts.join(" "),
  };
};

export const initiateSubscriptionPayment = async ({
  tenantId,
  planId,
  email,
  phone,
  firstName,
  lastName,
}) => {
  try {
    const payload = {
      tenantId: Number(tenantId) || 0,
      planId: Number(planId) || 0,
      email: String(email || ""),
      phone: String(phone || ""),
      firstName: String(firstName || ""),
      lastName: String(lastName || ""),
    };

    const res = await fetch(`${API_BASE_URL}/SubscriptionPayment/initiate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
      body: JSON.stringify(payload),
    });

    const data = await safeJson(res);
    const paymentUrl = extractPaymentUrl(data);

    if (!paymentUrl) {
      return {
        success: false,
        error: "لم يتم إرجاع رابط الدفع من مزود الدفع",
        data,
      };
    }

    return { success: true, paymentUrl, data };
  } catch (e) {
    console.error("initiateSubscriptionPayment error:", e);
    return { success: false, error: e.message || "فشل بدء عملية دفع الاشتراك" };
  }
};

const FAILED_STATUSES = ["failed", "fail", "cancelled", "canceled", "error", "declined", "rejected"];
const PENDING_STATUSES = ["pending", "processing", "initiated", "created", "new", "waiting"];

const unwrapPaymentStatusPayload = (data) => {
  if (!data || typeof data !== "object") return data;
  const nested = data.data;
  if (nested && typeof nested === "object" && (nested.orderId || nested.paymentStatus || nested.isPaid != null)) {
    return nested;
  }
  return data;
};

export const isSubscriptionPaymentPaid = (payload) => {
  const data = unwrapPaymentStatusPayload(payload) || {};
  const status = String(data.paymentStatus || data.PaymentStatus || "").toLowerCase();
  if (FAILED_STATUSES.includes(status)) return false;
  return data.isPaid === true || data.isPaid === 1 || String(data.isPaid).toLowerCase() === "true";
};

export const isSubscriptionPaymentPending = (payload) => {
  const data = unwrapPaymentStatusPayload(payload) || {};
  if (isSubscriptionPaymentPaid(data)) return false;
  const status = String(data.paymentStatus || data.PaymentStatus || data.status || "").toLowerCase();
  return PENDING_STATUSES.includes(status);
};

export const isSubscriptionPaymentFailed = (payload) => {
  const data = unwrapPaymentStatusPayload(payload) || {};
  if (isSubscriptionPaymentPaid(data)) return false;
  if (data.isPaid === false || data.isPaid === 0 || String(data.isPaid).toLowerCase() === "false") {
    const status = String(data.paymentStatus || data.PaymentStatus || data.status || "").toLowerCase();
    if (!status || FAILED_STATUSES.includes(status)) return true;
  }
  const status = String(data.paymentStatus || data.PaymentStatus || data.status || "").toLowerCase();
  return FAILED_STATUSES.includes(status);
};

export const getSubscriptionPaymentStatus = async (orderId) => {
  if (!orderId) {
    return { success: false, error: "رقم الطلب غير موجود" };
  }

  try {
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");
    const res = await fetch(
      `${API_BASE_URL}/SubscriptionPayment/status/${encodeURIComponent(orderId)}`,
      {
        method: "GET",
        headers: {
          Accept: "*/*",
          Authorization: token ? `Bearer ${token}` : "",
        },
      }
    );

    const data = await safeJson(res);
    const payload = unwrapPaymentStatusPayload(data);

    return {
      success: true,
      data: payload,
      isPaid: isSubscriptionPaymentPaid(payload),
      isPending: isSubscriptionPaymentPending(payload),
      isFailed: isSubscriptionPaymentFailed(payload),
    };
  } catch (e) {
    console.error("getSubscriptionPaymentStatus error:", e);
    return {
      success: false,
      error: e.message || "فشل التحقق من حالة دفع الاشتراك",
      isPaid: false,
      isPending: false,
      isFailed: true,
    };
  }
};

export const cancelCustomerOrder = async (tenantId, orderId) => {
  if (!orderId)
    return { success: false, error: "رقم الطلب غير موجود" };

  try {
    const res = await fetch(
      `${API_BASE_URL}/DeleteTenantOrders?tenantId=${tenantId}&orderId=${orderId}`,
      { method: "DELETE" }
    );

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    return { success: true };

  } catch {
    return { success: false, error: "فشل حذف الطلب" };
  }
};




export const updateProductVariantSafe = (products, itemId, variantId) => {
  return products.map(p => {
    if (Number(p.id) !== Number(itemId)) return p;

    const v = p.variants.find(x => Number(x.variantId) === Number(variantId));
    if (!v) return p;

    return {
      ...p,
      selectedVariant: { ...v }
    };
  });
};



